import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { PaginationList } from 'types/common'
import {
  getReportGeneral,
  getReportGroups,
  getReportProcess,
  getReportCollection,
  getReportAction,
  getReportProcessTable,
  getReportCollectionTable,
  getReportGroupsTable,
  getReportActionTable,
  getReportProcessDetail,
  getReportCollectionDetail,
  getReportGroupDetail,
  getCompanyReport,
  getCompanyReportDetail,
  getCompanyReportTable
} from 'API/report'
import {
  ICompanyDetail,
  ICompanyReport,
  ICompanyReportPayload,
  ICompanyReportTable,
  IReportAction,
  IReportActionPayload,
  IReportActionTable,
  IReportCollectionPayload,
  IReportCollectionResponse,
  IReportDetail,
  IReportDetailPayload,
  IReportGeneralPayload,
  IReportGeneralResponse,
  IReportGroup,
  IReportGroupPayload,
  IReportGroupTable,
  IReportProcessPayload,
  IReportProcessResponse
} from 'constants/report'
import { EPeriod } from 'constants/report/period'
import { IReportCollectionTable } from 'interfaces/collection'
import { IReportProcessTable } from 'interfaces/process'

class ReportStore {
  rootStore: RootStore

  generalInformation: IReportGeneralResponse | null = null
  reportProcess: IReportProcessResponse | null = null
  reportProcessTable?: PaginationList<IReportProcessTable>
  reportGroups: IReportGroup | null = null
  reportGroupsTable?: PaginationList<IReportGroupTable>
  reportCollection: IReportCollectionResponse | null = null
  reportCollectionTable?: PaginationList<IReportCollectionTable>
  reportAction: IReportAction | null = null
  reportActionTable?: PaginationList<IReportActionTable>

  reportPeriod: EPeriod = EPeriod.THIS_WEEK
  reportDetail: IReportDetail | null = null

  companyReport: ICompanyReport | null = null
  companyReportTable?: PaginationList<ICompanyReportTable>
  companyReportDetail?: ICompanyDetail

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public setReportPeriod(value: EPeriod): void {
    this.reportPeriod = value
  }

  public async getReportGeneralInformation(payload: IReportGeneralPayload): Promise<void> {
    const reportGeneralInformation = await getReportGeneral(payload)
    this.generalInformation = reportGeneralInformation
  }

  public async getReportProcess(payload: IReportProcessPayload): Promise<void> {
    const reportProcess = await getReportProcess(payload)
    this.reportProcess = reportProcess
  }

  public async getReportProcessTable(payload: IReportProcessPayload): Promise<void> {
    const reportProcessTable = await getReportProcessTable(payload)

    this.reportProcessTable = reportProcessTable
  }

  public async fetchReportGroups(payload: IReportGroupPayload) {
    this.reportGroups = await getReportGroups(payload)
    return this.reportGroups
  }

  public async fetchReportGroupTable(payload: IReportGroupPayload) {
    this.reportGroupsTable = await getReportGroupsTable(payload)
    return this.reportGroupsTable
  }

  public async getReportCollection(payload: IReportCollectionPayload): Promise<void> {
    const reportCollection = await getReportCollection(payload)
    this.reportCollection = reportCollection
  }

  public async getReportCollectionTable(payload: IReportCollectionPayload): Promise<void> {
    const reportCollectionTable = await getReportCollectionTable(payload)

    this.reportCollectionTable = reportCollectionTable
  }

  public async fetchReportAction(payload: IReportActionPayload): Promise<void> {
    const reportAction = await getReportAction(payload)
    this.reportAction = reportAction
  }

  public async fetchReportActionTable(payload: IReportActionPayload): Promise<void> {
    const reportActionTable = await getReportActionTable(payload)
    this.reportActionTable = reportActionTable
  }

  public async fetchSummaryReport(startDate: Date, endDate: Date): Promise<void> {
    await Promise.all([
      this.getReportGeneralInformation({ startDate, endDate }),
      this.getReportProcess({ startDate, endDate }),
      this.getReportCollection({ startDate, endDate }),
      this.fetchReportGroups({ startDate, endDate }),
      this.fetchReportAction({ startDate, endDate })
    ])
  }

  public async fetchReportProcessDetail(processId: number, payload: IReportDetailPayload) {
    const response = await getReportProcessDetail(processId, payload)
    this.reportDetail = response
  }

  public async fetchReportCollectionDetail(id: number, payload: IReportDetailPayload) {
    const response = await getReportCollectionDetail(id, payload)
    this.reportDetail = response
  }

  public async fetchReportGroupDetail(groupId: number, payload: IReportDetailPayload) {
    const response = await getReportGroupDetail(groupId, payload)
    this.reportDetail = response
  }

  public async fetchCompanyReport(payload: ICompanyReportPayload): Promise<void> {
    const response = await getCompanyReport(payload)
    this.companyReport = response
  }

  public async fetchCompanyReportTable(payload: ICompanyReportPayload): Promise<void> {
    const response = await getCompanyReportTable(payload)
    this.companyReportTable = response
  }

  public async fetchCompanyReportDetail(id: number, payload: IReportGeneralPayload): Promise<void> {
    const response = await getCompanyReportDetail(id, payload)
    this.companyReportDetail = response
  }
}

export default ReportStore
